// src/features/task/TaskDependencies.tsx
// Dependencies section for the task detail page: list what blocks / is blocked
// by / relates to this task, add and remove links, and jump to the full graph.
// Wraps ListDependencies / AddDependency / RemoveDependency.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useTask, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
} from '@react/components';
import { Select } from '@/components/Select';
import { useScope } from '@/hooks/useScope';
import { taskApi } from '@core/api';
import type { Task } from '@core/types/task';
import { formatEnumLabel, getEntityId } from '@/features/task/logic';
import { DEPENDENCY_TYPES, errMsg, makeEntity } from '@/features/task/panelHelpers';

interface TaskDependenciesProps {
  taskId: string;
  editable?: boolean;
}

export const TaskDependencies = observer(({ taskId, editable = true }: TaskDependenciesProps) => {
  const store = useTask();
  const { t } = useI18n();
  const scope = useScope();

  const [deps, setDeps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const [targetId, setTargetId] = useState('');
  const [targetLabel, setTargetLabel] = useState('');
  const [depType, setDepType] = useState<string>(DEPENDENCY_TYPES[0]);
  const [pasteMode, setPasteMode] = useState(false);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Task[]>([]);
  const [searching, setSearching] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res: any = await store.ListDependencies(taskId);
      // An empty list serializes as the bare wrapper `{}`, which is truthy and
      // would slip through an `||` chain to reach `.map` as an object.
      const list = res?.dependencies?.dependencies ?? res?.dependencies;
      setDeps(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(errMsg(e, t('task.deps.loadError')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (taskId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  // Debounced task search for the autocomplete target picker.
  useEffect(() => {
    if (pasteMode || !query.trim()) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const handle = setTimeout(() => {
      taskApi.ListTasks({
        pagination: { limit: 10, cursor: '', direction: 'forward' },
        sort: [],
        filters: { search_query: query.trim() } as any,
      } as any)
        .then((res) => {
          if (cancelled) return;
          setSearchResults((res?.tasks?.tasks || []).filter((tk: Task) => getEntityId(tk) !== taskId));
        })
        .catch(() => { if (!cancelled) setSearchResults([]); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [query, pasteMode, taskId]);

  const pickTarget = (tk: Task) => {
    setTargetId(getEntityId(tk));
    setTargetLabel(tk.title || '');
    setQuery('');
    setSearchResults([]);
  };

  const resetForm = () => {
    setTargetId('');
    setTargetLabel('');
    setQuery('');
    setSearchResults([]);
    setPasteMode(false);
    setDepType(DEPENDENCY_TYPES[0]);
    setFormOpen(false);
  };

  const submit = async () => {
    const id = targetId.trim();
    if (!id) {
      setError(t('task.deps.targetRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await store.AddDependency(taskId, {
        depends_on: makeEntity(id, 'task', scope),
        type: depType as any,
      } as any);
      resetForm();
      await load();
    } catch (e) {
      setError(errMsg(e, t('task.deps.saveError')));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (depId: string) => {
    setRemovingId(depId);
    setError(null);
    try {
      await store.RemoveDependency(taskId, depId);
      await load();
    } catch (e) {
      setError(errMsg(e, t('task.deps.removeError')));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('task.dependencies')}</CardTitle>
        <div className="flex items-center gap-2">
          <Link to={`/tasks/graph?task=${taskId}`} className="text-xs text-primary hover:underline">
            {t('task.deps.viewGraph')}
          </Link>
          {editable && !formOpen && (
            <Button size="sm" variant="secondary" onClick={() => setFormOpen(true)}>
              {t('task.deps.add')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <Alert variant="error">{error}</Alert>}

        {formOpen && (
          <div className="space-y-2 rounded-md border border-border p-3">
            <Select value={depType} onChange={(e) => setDepType(e.target.value)}>
              {DEPENDENCY_TYPES.map((dt) => (
                <option key={dt} value={dt}>{t(`task.deps.type.${dt}`)}</option>
              ))}
            </Select>

            {pasteMode ? (
              <input
                type="text"
                aria-label={t('task.deps.targetId')}
                placeholder={t('task.deps.targetIdPlaceholder')}
                value={targetId}
                onChange={(e) => { setTargetId(e.target.value); setTargetLabel(''); }}
                className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
              />
            ) : targetId ? (
              <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-2 py-1.5">
                <span className="text-sm text-foreground truncate">{targetLabel || targetId}</span>
                <button
                  type="button"
                  onClick={() => { setTargetId(''); setTargetLabel(''); }}
                  className="text-xs text-muted-foreground hover:text-destructive shrink-0"
                >
                  {t('common.remove')}
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  aria-label={t('task.deps.searchTasks')}
                  placeholder={t('task.deps.searchPlaceholder')}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
                />
                {query.trim() && (
                  <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-border bg-popover shadow-sm">
                    {searching ? (
                      <p className="px-2 py-1.5 text-xs text-muted-foreground">{t('common.searching')}</p>
                    ) : searchResults.length === 0 ? (
                      <p className="px-2 py-1.5 text-xs text-muted-foreground">{t('common.noResults')}</p>
                    ) : (
                      searchResults.map((tk) => {
                        const tid = getEntityId(tk);
                        return (
                          <button
                            key={tid}
                            type="button"
                            onClick={() => pickTarget(tk)}
                            className="block w-full truncate px-2 py-1.5 text-left text-sm text-foreground hover:bg-muted"
                          >
                            {tk.title || tid.substring(0, 8)}
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}

            {!targetId && (
              <button
                type="button"
                onClick={() => { setPasteMode(!pasteMode); setQuery(''); setSearchResults([]); }}
                className="text-xs text-primary hover:underline"
              >
                {pasteMode ? t('task.deps.searchTasks') : t('task.deps.pasteId')}
              </button>
            )}

            <div className="flex gap-1.5">
              <Button size="sm" onClick={submit} isLoading={busy} disabled={!targetId.trim()}>
                {t('task.deps.add')}
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={resetForm}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="h-12 animate-pulse rounded-md bg-muted" aria-busy="true" />
        ) : deps.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('task.deps.empty')}</p>
        ) : (
          <div className="divide-y divide-border">
            {deps.map((dep: any, idx: number) => {
              const depId = dep.entity?.id || dep.id || '';
              const targetEntityId = dep.depends_on?.id || '';
              return (
                <div key={depId || idx} className="py-3 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant="secondary" size="sm">
                      {dep.type ? formatEnumLabel(String(dep.type), 'DEPENDENCY_TYPE_') : t('task.related')}
                    </Badge>
                    <Link
                      to={`/tasks/${targetEntityId}`}
                      className="text-sm text-primary hover:underline truncate"
                    >
                      {dep.depends_on?.urn || targetEntityId || t('common.unknown')}
                    </Link>
                  </div>
                  {editable && depId && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive shrink-0"
                      isLoading={removingId === depId}
                      onClick={() => remove(depId)}
                    >
                      {t('common.remove')}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
});

export default TaskDependencies;
